/**
 * JSON取り込みのキー読み出しヘルパー（#3372）。
 *
 * JSONのキーは snake_case を正とする（Firestore の保存形式・外部公開を想定。#3195）。
 * ただし旧 camelCase 表記で書かれたJSONも読めるようにする。
 * 手元に保存した古いJSONや、他環境からコピーしたJSONを取り込んだときに
 * 設定が黙って初期値へ戻るのを防ぐため。
 *
 * もとは ItemListManager.tsx のローカル関数だったが、子パターンのJSON取り込み（#3372）でも
 * 同じ両対応が必要になったため切り出した。コピーすると片方だけ直して挙動が分かれる。
 */

/** snake_case を優先し、無ければ camelCase から読む */
export const pickRaw = (src: Record<string, unknown>, snake: string, camel: string): unknown =>
  src[snake] !== undefined ? src[snake] : src[camel];

/** 数値として読む。数値以外は undefined（保存時バリデーションに委ねる） */
export const pickNumber = (
  src: Record<string, unknown>,
  snake: string,
  camel: string,
): number | undefined => {
  const value = pickRaw(src, snake, camel);
  return typeof value === 'number' ? value : undefined;
};

/** 文字列として読む。文字列以外は空文字 */
export const pickString = (src: Record<string, unknown>, snake: string, camel: string): string => {
  const value = pickRaw(src, snake, camel);
  return typeof value === 'string' ? value : '';
};

/** boolean として読む。boolean 以外は undefined（「指定なし」と区別するため） */
export const pickBoolean = (
  src: Record<string, unknown>,
  snake: string,
  camel: string,
): boolean | undefined => {
  const value = pickRaw(src, snake, camel);
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * 列挙値として読む。候補に無い値は undefined へ倒す。
 *
 * 不正な値をそのまま通すと Firestore に未定義の値が入り、
 * 画面の選択肢に無い状態になって直せなくなるため。
 */
export const pickEnum = <T extends string>(
  src: Record<string, unknown>,
  snake: string,
  camel: string,
  allowed: readonly T[],
): T | undefined => {
  const value = pickRaw(src, snake, camel);
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
};

/**
 * 文字列配列として読む。配列でなければ undefined、
 * 要素のうち文字列でないものは落とす。
 */
export const pickStringArray = (
  src: Record<string, unknown>,
  snake: string,
  camel: string,
): string[] | undefined => {
  const value = pickRaw(src, snake, camel);
  if (!Array.isArray(value)) return undefined;
  return value.filter((v): v is string => typeof v === 'string');
};

/** #3357 の検証設定のうち、JSONへ書き出す対象。親項目・明細の列で共通 */
type ValidationExportSource = {
  charType?: string;
  pattern?: string;
  patternMessage?: string;
  dateNoPast?: boolean;
  dateNoFuture?: boolean;
  datePastLimit?: number;
  datePastUnit?: string;
  dateFutureLimit?: number;
  dateFutureUnit?: string;
  reviewChoiceValues?: string[];
  reviewLowConfidence?: boolean; // 読み取り精度の要注意（#3384）
};

/**
 * 文字種・入力パターン・日付範囲・要注意選択肢（#3357）をJSONへ書き出す（#3372）。
 *
 * これらが書き出されないと、JSONを開いてそのまま反映するだけで設定が消える
 * （書き出しに出ない → 取り込みでも読まれない → undefined で保存される）。
 *
 * 値が設定されているときだけ出力する（既存フィールドと同じ流儀）。
 * 親項目と明細の列で同じ形のため共通化する。
 */
export const buildValidationExport = (src: ValidationExportSource): Record<string, unknown> => ({
  ...(src.charType ? { char_type: src.charType } : {}),
  ...(src.pattern ? { pattern: src.pattern } : {}),
  ...(src.patternMessage ? { pattern_message: src.patternMessage } : {}),
  ...(src.dateNoPast ? { date_no_past: true } : {}),
  ...(src.dateNoFuture ? { date_no_future: true } : {}),
  ...(src.datePastLimit !== undefined ? { date_past_limit: src.datePastLimit } : {}),
  ...(src.datePastUnit ? { date_past_unit: src.datePastUnit } : {}),
  ...(src.dateFutureLimit !== undefined ? { date_future_limit: src.dateFutureLimit } : {}),
  ...(src.dateFutureUnit ? { date_future_unit: src.dateFutureUnit } : {}),
  ...(src.reviewChoiceValues && src.reviewChoiceValues.length > 0
    ? { review_choice_values: src.reviewChoiceValues }
    : {}),
  // 読み取り精度の要注意（#3384）。デフォルト ON のため、OFF のときだけ出力する
  ...(src.reviewLowConfidence === false ? { review_low_confidence: false } : {}),
});
